import { useAppDispatch, useAppSelector } from "app/hooks";
import { setFilters } from "features/krpano/details-slice";
import { useCallback, useEffect, useState } from "react";
import { RootState } from "app/store";
import { Dropdown } from "shared/components/Dropdown";
import { DropdownProps } from "shared/components/Dropdown/model";
import { ZFilterPlugin } from "krpano";
import { BaseDropdownWrapperProps } from "../colourFiltersModels";

interface DropdownWrapperProps extends BaseDropdownWrapperProps {
  property: Extract<
    keyof RootState["krpano"]["plugins"]["zfilter"],
    "rgbvimode"
  >;
}

export const DropdownWrapper: React.FC<DropdownWrapperProps> = ({
  property,
}) => {
  // Plugin, State, and Dispatch
  const plugin = window.krpano.plugin.getItem<ZFilterPlugin>("zfilter");
  const pluginNotLoaded = !plugin?.loaded;
  const value = useAppSelector(
    (state) => state.krpano.plugins.zfilter[property]
  );
  const dispatch = useAppDispatch();

  // Callbacks
  const onChange: DropdownProps["onChange"] = useCallback(
    (option) => {
      dispatch(setFilters({ [property]: option.value }));
      // Set in krpano
      try {
        if (plugin) {
          plugin[property] = option.value;
        }
      } catch (error) {
        console.error("Error setting dropdown value in plugin:", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, pluginNotLoaded, property]
  );

  const [options, setOptions] = useState<ReturnType<typeof formatOption>[]>([]);

  // Build options from plugin when loaded
  useEffect(() => {
    if (plugin) {
      switch (property) {
        case "rgbvimode":
          setOptions(
            plugin["rgbvimodes"]
              ?.split("|")
              .map((mode: string) => ({ label: mode, value: mode })) ?? []
          );

          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pluginNotLoaded, property]);

  return (
    <Dropdown
      value={formatOption(value)}
      options={options}
      onChange={onChange}
      roundedRectangle
      small
      isDisabled={pluginNotLoaded}
      // NOTE: `inPortal` required as dropdown is inside accordion and needs to
      // allow the overflow to be visible
      inPortal
    />
  );
};

const formatOption = (option: string): DropdownProps["options"][number] => ({
  label: option,
  value: option,
});
